import { useEffect, useRef } from 'react';
import { useEstaOnline } from '@/features/auth/AuthContext';
import { aoProdutoBloqueado } from '@/features/complementos/avisoBloqueio';
import { BloqueioProduto } from '@/features/complementos/components/BloqueioProduto';
import type { ProdutoEntitlement } from '@/features/complementos/hooks/useProdutoEntitlement';
import { PdvPage } from '../pages/PdvPage';

/** Código do PDV no catálogo de produtos add-on do servidor. */
export const CODIGO_PDV = 'pdv';

const DESCRICAO_PADRAO =
  'O PDV é a venda rápida de balcão: abre e fecha caixa por colaborador, vende com leitor de ' +
  'código de barras, aceita várias formas de pagamento e fiado, faz devolução e imprime o ' +
  'cupom na térmica de 58 mm.';

type Props = {
  /** Estado do direito de uso, vindo do `useProdutoEntitlement('pdv')` do App. */
  acesso: ProdutoEntitlement;
};

/**
 * Porta do PDV. Sem o produto, NENHUMA tela do PDV é montada — nem
 * caixa, nem histórico, nem relatório: elas fariam requests que o backend
 * recusa (`pdv.inativo`) e o operador veria só uma fileira de erros.
 *
 * - Primeira verificação em curso: carregamento neutro, para quem tem o
 *   produto não ver a trava piscar.
 * - Revalidação (entrar na aba, a cada 2 min): o PDV continua montado; o
 *   carrinho não se perde por causa de uma checagem de rotina.
 * - Uma rota do PDV respondeu `pdv.inativo`/`pdv.sem_conexao` no meio do
 *   uso: revalida na hora e, confirmado, troca a tela pelo bloqueio.
 */
export function AcessoPdv({ acesso }: Props) {
  const { ativo, entitlement, carregando, erro, verificandoPrimeiraVez, recarregar } = acesso;
  const online = useEstaOnline();
  const revalidando = useRef(false);

  useEffect(
    () =>
      aoProdutoBloqueado(CODIGO_PDV, () => {
        // Várias telas do PDV falham juntas quando o acesso cai; uma
        // revalidação por vez basta.
        if (revalidando.current) return;
        revalidando.current = true;
        // Offline, forçar só trocaria a cópia assinada por "sem conexão".
        void Promise.resolve(recarregar(online)).finally(() => {
          revalidando.current = false;
        });
      }),
    [recarregar, online],
  );

  if (verificandoPrimeiraVez) {
    return (
      <div className="pdv pdv--carregando" role="status">
        Verificando acesso ao PDV…
      </div>
    );
  }

  if (!ativo) {
    return (
      <BloqueioProduto
        codigo={CODIGO_PDV}
        nomePadrao="PDV"
        descricaoPadrao={DESCRICAO_PADRAO}
        entitlement={entitlement}
        carregando={carregando}
        erro={erro}
        recarregar={recarregar}
      />
    );
  }

  return <PdvPage />;
}
